import { inject, Injectable } from '@angular/core';
import { GoodsSkuControllerService, SkuTaxRateVO } from '@app/api/goods-service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { ProductTaxInheritedValue, ProductTaxSource } from './product-tax.model';

export interface SkuTaxResolution {
  configuredTaxId: number | null;
  effective: ProductTaxInheritedValue;
}

@Injectable({ providedIn: 'root' })
export class SkuTaxResolutionService {
  private readonly goodsSkuController = inject(GoodsSkuControllerService);

  resolve(skuCode: string): Observable<SkuTaxResolution> {
    return this.resolveMany([skuCode]).pipe(
      map((resolutions) => {
        const resolution = resolutions.get(skuCode);
        if (!resolution) throw new Error('SKU_TAX_RESOLUTION_FAILED');
        return resolution;
      }),
    );
  }

  resolveMany(skuCodes: string[]): Observable<Map<string, SkuTaxResolution>> {
    if (!skuCodes.length) return of(new Map());
    return this.goodsSkuController.lookupSkuTaxRates({ skuCodes }).pipe(
      switchMap((response: Array<SkuTaxRateVO> | Blob) =>
        response instanceof Blob
          ? (from(BlobConverterUtil.convertToJson(response)) as Observable<Array<SkuTaxRateVO>>)
          : of(response),
      ),
      map((items) => {
        const result = new Map<string, SkuTaxResolution>();
        for (const resolved of items) {
          if (!resolved.skuCode || resolved.errorCode) {
            throw new Error(resolved.errorCode || 'SKU_TAX_RESOLUTION_FAILED');
          }
          const source = this.toSource(resolved.taxSource);
          result.set(resolved.skuCode, {
            configuredTaxId: source === 'SKU_OVERRIDE' ? (resolved.taxId ?? null) : null,
            effective: {
              taxId: resolved.taxId ?? null,
              taxCode: resolved.taxCode ?? null,
              taxRate: resolved.taxRate ?? null,
              isPriceIncludeVAT: resolved.priceIncludeVat ?? null,
              source,
            },
          });
        }
        if (skuCodes.some((skuCode) => !result.has(skuCode))) {
          throw new Error('SKU_TAX_RESOLUTION_FAILED');
        }
        return result;
      }),
    );
  }

  private toSource(value?: string): ProductTaxSource {
    switch (value) {
      case 'SKU_OVERRIDE':
      case 'SPU_OVERRIDE':
      case 'CATEGORY':
      case 'SYSTEM_DEFAULT':
        return value;
      default:
        return 'UNCONFIGURED';
    }
  }
}
